import { createClient } from '@supabase/supabase-js';
import { Database } from '../types/supabase';

// Load environment variables
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Validate environment variables
if (!supabaseUrl || !supabaseAnonKey) {
  const missingVars = [];
  if (!supabaseUrl) missingVars.push('VITE_SUPABASE_URL');
  if (!supabaseAnonKey) missingVars.push('VITE_SUPABASE_ANON_KEY');
  
  const errorMessage = `Missing required environment variables: ${missingVars.join(', ')}. Please check your .env file.`;
  console.error(errorMessage);
  throw new Error(errorMessage);
}

// Create the Supabase client with enhanced session handling
export const supabase = createClient<Database>(
  supabaseUrl,
  supabaseAnonKey,
  {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: true,
      storage: window.localStorage,
      storageKey: 'sb-auth-token',
      flowType: 'pkce',
    },
    global: {
      headers: {
        'X-Client-Info': 'supabase-js/2.39.3',
      },
    },
  }
);

// Clear all auth-related data from storage
function clearAuthData() {
  const storageKeys = [
    'sb-auth-token',
    'supabase.auth.token',
    'supabase.auth.refreshToken',
    'supabase.auth.user'
  ];
  
  storageKeys.forEach(key => {
    window.localStorage.removeItem(key);
    window.sessionStorage.removeItem(key);
  });
}

// Add auth state change listener with improved session handling
supabase.auth.onAuthStateChange(async (event, session) => {
  if (event === 'SIGNED_OUT' || event === 'USER_DELETED') {
    clearAuthData();
  } else if ((event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') && session) {
    try {
      // Store the complete session object with expiration
      const sessionData = {
        access_token: session.access_token,
        refresh_token: session.refresh_token,
        expires_at: Math.floor(Date.now() / 1000) + (session.expires_in || 3600),
        user: session.user,
      };
      window.localStorage.setItem('sb-auth-token', JSON.stringify(sessionData));
      
      // Store refresh token separately for backup
      if (session.refresh_token) {
        window.localStorage.setItem('supabase.auth.refreshToken', session.refresh_token);
      }
    } catch (err) {
      console.error('Failed to persist session:', err);
      clearAuthData();
    }
  }
});

// Enhanced session refresh function with retry logic and exponential backoff
async function refreshSession(retryCount = 3): Promise<any> {
  let lastError;
  
  // Clear any stale session data before attempting refresh
  const { data: { session: currentSession } } = await supabase.auth.getSession();
  if (!currentSession) {
    clearAuthData();
    throw new Error('No active session found');
  }
  
  for (let i = 0; i < retryCount; i++) {
    try {
      const { data: { session }, error } = await supabase.auth.refreshSession();
      if (error) throw error;
      if (!session) throw new Error('Failed to refresh session');
      return session;
    } catch (err) {
      console.error(`Session refresh attempt ${i + 1} failed:`, err);
      lastError = err;
      
      // Wait before retrying (exponential backoff)
      if (i < retryCount - 1) {
        await new Promise(resolve => setTimeout(resolve, Math.pow(2, i) * 1000));
      }
    }
  }
  
  // Clear auth data after all retries fail
  clearAuthData();
  throw lastError;
}

// Function to verify connection and handle session refresh
export async function verifyConnection() {
  try {
    // Check auth state first
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError || !session) {
      clearAuthData();
      throw new Error('Sessão não encontrada');
    }

    // Try a simple authenticated request
    const { error } = await supabase
      .from('airports')
      .select('id')
      .limit(1);

    if (error) {
      if (error.message?.includes('JWT') || error.message?.includes('token')) {
        try {
          await refreshSession();
          
          // Retry the request with refreshed session
          const retryResponse = await supabase
            .from('airports')
            .select('id')
            .limit(1);

          if (retryResponse.error) {
            throw retryResponse.error;
          }
        } catch (refreshError) {
          clearAuthData();
          await supabase.auth.signOut();
          throw new Error('Sessão expirada. Por favor, faça login novamente.');
        }
      } else {
        throw error;
      }
    }

    return true;
  } catch (err) {
    const errorMessage = err instanceof Error ? err.message : 'Unknown error';
    
    if (errorMessage.includes('Failed to fetch')) {
      console.error(`
Connection Error: Unable to reach Supabase

Please check:
1. Your internet connection
2. Supabase project status at ${supabaseUrl}
3. CORS Configuration in Supabase Dashboard:
   - Go to Project Settings > API > CORS Configuration
   - Add: http://localhost:5173, http://127.0.0.1:5173
   - Save and wait a few minutes
   - Clear browser cache and refresh

For more help, visit: https://supabase.com/docs/guides/api/cors
`);
      throw new Error('Erro de conexão com o servidor. Por favor, verifique sua conexão com a internet e as configurações CORS.');
    }

    throw err;
  }
}

// More frequent session refresh (every 4 minutes)
setInterval(async () => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (session) {
      await refreshSession();
    }
  } catch (err) {
    console.error('Background session refresh failed:', err);
  }
}, 4 * 60 * 1000);

// Export connection check function for components
export function getSupabaseConfig() {
  return {
    url: supabaseUrl,
    hasKey: !!supabaseAnonKey,
    isVerified: false
  };
}